/* @HEADER
 * @version 1.1.896 | 2026-10-24
 * @purpose ZVX-DEV-8269 — the per-person record of which removals from an organisation the person was already told about on this device, so the banner is shown once per removal; a device that cannot keep the mark does not tell.
 * @sidecar leftOrganisationTold.ts.header.md
 * @layer app | @hex adapter | @ctx auth
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8269

/**
 * ZVX-DEV-8269 -- which removals from an organisation this person has already been TOLD about on this device, so the banner
 * is shown once for each removal and not on every membership refresh.
 *
 * Kept per person under its own key, as `{ [organisationId]: ISO time told }`. An organisation that is no longer «left» (the
 * person was added back) is forgotten, so a second removal is told again. Where the device cannot keep the mark the person is
 * NOT told: a banner that comes back every thirty seconds is worse than one that does not come. Free of module state; every
 * function takes the storage it reads and writes.
 */

export type ToldStorage = Pick<Storage, 'getItem' | 'setItem'>

export const LEFT_ORGANISATION_TOLD_KEY_PREFIX = 'zvenix.auth.leftOrganisationTold.'

function keyFor(userId: string): string {
  return `${LEFT_ORGANISATION_TOLD_KEY_PREFIX}${userId}`
}

export function readTold(storage: Pick<Storage, 'getItem'> | null, userId: string | null): Record<string, string> {
  if (!storage || !userId) return {}
  try {
    const raw = storage.getItem(keyFor(userId))
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Record<string, string> = {}
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (id.length > 0 && typeof at === 'string') out[id] = at
    }
    return out
  } catch {
    return {}
  }
}

function write(storage: ToldStorage, userId: string, told: Record<string, string>): boolean {
  try {
    storage.setItem(keyFor(userId), JSON.stringify(told))
    // Read back: a storage that swallows the write would let the banner come back on every refresh.
    return JSON.stringify(readTold(storage, userId)) === JSON.stringify(told)
  } catch {
    return false
  }
}

/** Marks the organisations as told. True only when the mark is really kept: the caller shows the banner only then. */
export function markTold(storage: ToldStorage | null, userId: string | null, organisationIds: readonly string[], now: string): boolean {
  if (!storage || !userId || organisationIds.length === 0) return false
  const told = readTold(storage, userId)
  for (const id of organisationIds) told[id] = told[id] ?? now
  return write(storage, userId, told)
}

/** Forgets every mark except those of the organisations still left, so an organisation that comes back and goes again is told again. */
export function forgetToldExcept(storage: ToldStorage | null, userId: string | null, stillLeftIds: readonly string[]): void {
  if (!storage || !userId) return
  const told = readTold(storage, userId)
  const keep = new Set(stillLeftIds)
  const next: Record<string, string> = {}
  for (const [id, at] of Object.entries(told)) if (keep.has(id)) next[id] = at
  if (Object.keys(next).length === Object.keys(told).length) return
  write(storage, userId, next)
}
